<script setup>
/* global axios */
// Aba "Ligações BCVOZ (PABX)" das configurações do inbox: só o token da conta.
// A URL do PABX é fixa no backend (sempre a mesma) e o webphone é o widget nativo
// carregado globalmente do CDN (ver useBcvozWebphone) — não há URL aqui.
// Método SEPARADO e independente do Wavoip/AstraCall.
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const route = useRoute();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/kanban/bcvoz_tokens`;

const apiToken = ref('');
const enabled = ref(true);
const syncUsers = ref(false);
const hasToken = ref(false);
const botUser = ref('');
const botPassword = ref('');
const hasBotPassword = ref(false);
const saving = ref(false);

const load = async () => {
  try {
    const { data } = await axios.get(base, {
      params: { inbox_id: props.inbox.id },
    });
    const d = data.data;
    if (d) {
      enabled.value = d.enabled !== false;
      syncUsers.value = !!d.sync_users;
      hasToken.value = !!d.has_token;
      botUser.value = d.bot_user || '';
      hasBotPassword.value = !!d.has_bot_password;
    }
  } catch (e) {
    // mantém defaults
  }
};

const save = async () => {
  saving.value = true;
  try {
    await axios.post(base, {
      inbox_id: props.inbox.id,
      api_token: apiToken.value.trim(),
      enabled: enabled.value,
      sync_users: syncUsers.value,
      bot_user: botUser.value.trim(),
      bot_password: botPassword.value,
    });
    apiToken.value = '';
    hasToken.value = true;
    if (botPassword.value) hasBotPassword.value = true;
    botPassword.value = '';
    useAlert(t('INBOX_MGMT.BCVOZ.SAVED'));
  } catch (e) {
    useAlert(e?.response?.data?.error || t('INBOX_MGMT.BCVOZ.ERROR'));
  } finally {
    saving.value = false;
  }
};

onMounted(load);
</script>

<template>
  <div class="p-6 mx-6">
    <h4 class="text-base font-medium text-n-slate-12">
      {{ t('INBOX_MGMT.BCVOZ.TITLE') }}
    </h4>
    <p class="mt-1 mb-4 text-sm text-n-slate-11">
      {{ t('INBOX_MGMT.BCVOZ.DESC') }}
    </p>

    <label
      class="flex items-center gap-2 mb-4 text-sm font-medium text-n-slate-12"
    >
      <input v-model="enabled" type="checkbox" class="rounded border-n-weak" />
      {{ t('INBOX_MGMT.BCVOZ.ENABLED_LABEL') }}
    </label>

    <label class="block mb-1 text-sm font-medium text-n-slate-12">
      {{ t('INBOX_MGMT.BCVOZ.TOKEN_LABEL') }}
    </label>
    <input
      v-model="apiToken"
      type="text"
      :placeholder="
        hasToken
          ? t('INBOX_MGMT.BCVOZ.TOKEN_SET')
          : t('INBOX_MGMT.BCVOZ.TOKEN_PLACEHOLDER')
      "
      class="w-full max-w-lg px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
    />
    <p class="mt-1 mb-4 text-xs text-n-slate-10">
      {{ t('INBOX_MGMT.BCVOZ.TOKEN_HINT') }}
    </p>

    <label
      class="flex items-center gap-2 mb-1 text-sm font-medium text-n-slate-12"
    >
      <input
        v-model="syncUsers"
        type="checkbox"
        class="rounded border-n-weak"
      />
      {{ t('INBOX_MGMT.BCVOZ.SYNC_USERS_LABEL') }}
    </label>
    <p class="mt-1 mb-4 text-xs text-n-slate-10">
      {{ t('INBOX_MGMT.BCVOZ.SYNC_USERS_HINT') }}
    </p>

    <div class="pt-4 mt-2 mb-4 border-t border-n-weak">
      <h5 class="mb-1 text-sm font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.BCVOZ.BOT_TITLE') }}
      </h5>
      <p class="mb-3 text-xs text-n-slate-10">
        {{ t('INBOX_MGMT.BCVOZ.BOT_HINT') }}
      </p>

      <label class="block mb-1 text-sm font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.BCVOZ.BOT_USER_LABEL') }}
      </label>
      <input
        v-model="botUser"
        type="text"
        :placeholder="t('INBOX_MGMT.BCVOZ.BOT_USER_PLACEHOLDER')"
        class="w-full max-w-lg px-3 py-2 mb-3 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
      />

      <label class="block mb-1 text-sm font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.BCVOZ.BOT_PASSWORD_LABEL') }}
      </label>
      <input
        v-model="botPassword"
        type="password"
        autocomplete="new-password"
        :placeholder="
          hasBotPassword
            ? t('INBOX_MGMT.BCVOZ.BOT_PASSWORD_SET')
            : t('INBOX_MGMT.BCVOZ.BOT_PASSWORD_PLACEHOLDER')
        "
        class="w-full max-w-lg px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
      />
    </div>

    <NextButton
      :label="t('INBOX_MGMT.BCVOZ.SAVE')"
      blue
      sm
      :is-loading="saving"
      @click="save"
    />
  </div>
</template>
